import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiLock, FiArrowRight, FiHeart } from 'react-icons/fi';
import { useAuth } from '../../auth/AuthContext';
import { useToast } from '../../theme/components';
import { parseApiError } from '../../api/errors';
import { APP_NAME, APP_TAGLINE } from '../../config';
import './LoginSignup.css';

const LoginSignup = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const toast = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim() || !password) {
            toast.warning('Please enter both email and password.');
            return;
        }
        setIsLoading(true);
        try {
            await login(email.trim(), password);
            const dest = location.state?.from?.pathname || '/dashboard';
            navigate(dest, { replace: true });
        } catch (err) {
            toast.error(parseApiError(err, 'Login failed.'));
            setIsLoading(false);
        }
    };

    return (
        <div className={`ls-shell ${isLoading ? 'is-loading' : ''}`}>
            {/* Brand panel */}
            <aside className="ls-brand">
                <div className="ls-brand__inner">
                    <div className="ls-brand__badge">
                        <FiHeart size={20} />
                        <span>Public Health</span>
                    </div>
                    <h1 className="ls-brand__title">
                        Welcome to <span>{APP_NAME}</span>
                    </h1>
                    <p className="ls-brand__tagline">{APP_TAGLINE}</p>
                    <ul className="ls-brand__bullets" aria-hidden="true">
                        <li><span className="dot" /> Track child registrations &amp; growth</li>
                        <li><span className="dot" /> Manage vaccination schedules</li>
                        <li><span className="dot" /> Generate health reports</li>
                    </ul>
                </div>
                <svg className="ls-brand__art" viewBox="0 0 400 400" aria-hidden="true">
                    <defs>
                        <linearGradient id="ls-arc" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
                            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <circle cx="280" cy="120" r="180" fill="url(#ls-arc)" />
                    <circle cx="320" cy="320" r="120" fill="url(#ls-arc)" />
                </svg>
            </aside>

            {/* Form panel */}
            <main className="ls-panel">
                <div className="ls-card" aria-busy={isLoading}>
                    <header className="ls-card__header">
                        <h2>Sign in</h2>
                        <p>Use your clinic account to continue.</p>
                    </header>

                    <form className="ls-form" onSubmit={handleSubmit} noValidate>
                        <label className="ls-field">
                            <span className="ls-field__icon" aria-hidden="true"><FiMail size={18} /></span>
                            <input
                                type="email"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                                required
                                aria-label="Email address"
                            />
                        </label>

                        <label className="ls-field">
                            <span className="ls-field__icon" aria-hidden="true"><FiLock size={18} /></span>
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                                aria-label="Password"
                            />
                        </label>

                        <div className="ls-forgot" title="Contact your administrator to reset your password.">
                            Forgot password?
                            <button
                                type="button"
                                className="ls-forgot__btn"
                                disabled
                                aria-label="Forgot password is disabled"
                            >
                                Contact admin
                            </button>
                        </div>

                        <button
                            type="submit"
                            className="ls-submit"
                            disabled={isLoading}
                            aria-label="Sign in"
                        >
                            <span>{isLoading ? 'Signing in…' : 'Sign in'}</span>
                            {!isLoading && <FiArrowRight size={18} />}
                        </button>
                    </form>

                    <footer className="ls-card__footer">
                        <span>Pediatrack v1.0 · for clinic staff</span>
                    </footer>
                </div>
            </main>

            {isLoading && (
                <div className="ls-loading-overlay" aria-hidden="true">
                    <div className="ls-loading-spinner" />
                </div>
            )}
        </div>
    );
};

export default LoginSignup;
